import {isQueryBuilder, toQuery, type QueryBuilder} from './fluent.js';
import type {Query} from './types.js';

// eslint-disable-next-line @typescript-eslint/naming-convention
export const LOCAL_DATA_PROFILE_SCHEMA_VERSION = 1;

export type ReplicationAccess = 'public' | 'protected';
export type ReplicationScopeKind = 'document' | 'tree' | 'query';

export type ReferenceReplicationOptions = {
	mode: 'typed-data';
	maxDepth: number;
};

export type ReplicationScopeOptions = {
	access?: ReplicationAccess;
	maxDepth?: number;
	prune?: Array<Query | QueryBuilder>;
	references?: ReferenceReplicationOptions;
};

export type SerializedReplicationScope = {
	id: string;
	kind: ReplicationScopeKind;
	access: ReplicationAccess;
	anchorId?: string;
	anchorFormId?: string;
	maxDepth?: number;
	selector?: Query;
	prune: Query[];
	references?: ReferenceReplicationOptions;
};

export type SerializedLocalDataProfile = {
	schemaVersion: number;
	id: string;
	reevaluateEvery?: string;
	scopes: SerializedReplicationScope[];
};

type DurationUnit = 'seconds' | 'minutes' | 'hours' | 'days';

export class CadenceBuilder {
	constructor(private readonly _profile: LocalDataProfileBuilder, private readonly _value: number) {}

	seconds() {
		return this._finish('seconds');
	}

	minutes() {
		return this._finish('minutes');
	}

	hours() {
		return this._finish('hours');
	}

	days() {
		return this._finish('days');
	}

	private _finish(unit: DurationUnit) {
		return this._profile._setCadence(formatDuration(this._value, unit));
	}
}

export class LocalDataProfileBuilder {
	private readonly _id: string;
	private readonly _scopes: SerializedReplicationScope[] = [];
	private _reevaluateEvery?: string;

	constructor(id: string) {
		this._id = requireNonEmptyString(id, 'Local Data Profile ID');
	}

	reevaluateEvery(duration: string): this;
	reevaluateEvery(value: number): CadenceBuilder;
	reevaluateEvery(value: string | number): this | CadenceBuilder {
		if (typeof value === 'number') {
			requirePositiveInteger(value, 'Reevaluation cadence');
			return new CadenceBuilder(this, value);
		}

		return this._setCadence(normalizeIsoDuration(value));
	}

	document(id: string, anchorId: string, options: ReplicationScopeOptions = {}) {
		return this._appendScope({
			id,
			kind: 'document',
			access: options.access ?? 'public',
			anchorId: requireNonEmptyString(anchorId, `Scope '${id}' anchor`),
			prune: normalizePrune(id, options.prune),
			references: normalizeReferences(options.references, id),
		});
	}

	tree(id: string, anchorId: string, options: ReplicationScopeOptions = {}) {
		return this._appendScope({
			id,
			kind: 'tree',
			access: options.access ?? 'public',
			anchorId: requireNonEmptyString(anchorId, `Scope '${id}' anchor`),
			maxDepth: normalizeTreeDepth(options.maxDepth, id),
			prune: normalizePrune(id, options.prune),
			references: normalizeReferences(options.references, id),
		});
	}

	treeByForm(id: string, anchorFormId: string, options: ReplicationScopeOptions = {}) {
		return this._appendScope({
			id,
			kind: 'tree',
			access: options.access ?? 'protected',
			anchorFormId: requireNonEmptyString(anchorFormId, `Scope '${id}' anchor form`),
			maxDepth: normalizeTreeDepth(options.maxDepth, id),
			prune: normalizePrune(id, options.prune),
			references: normalizeReferences(options.references, id),
		});
	}

	scope(id: string, selector: Query | QueryBuilder, options: ReplicationScopeOptions = {}) {
		const normalizedId = requireNonEmptyString(id, 'Replication Scope ID');
		const query = normalizeSelector(selector, normalizedId);
		assertDescendantSelector(query, normalizedId);
		return this._appendScope({
			id: normalizedId,
			kind: 'query',
			access: options.access ?? 'protected',
			selector: query,
			prune: normalizePrune(normalizedId, options.prune),
			references: normalizeReferences(options.references, normalizedId),
		});
	}

	_setCadence(duration: string) {
		this._reevaluateEvery = duration;
		return this;
	}

	toProfile(): SerializedLocalDataProfile {
		if (this._scopes.length === 0) {
			throw new Error(`Local Data Profile '${this._id}' requires at least one Replication Scope`);
		}

		if (!this._reevaluateEvery && this._scopes.some(scope => scopeUsesRelativeTime(scope))) {
			throw new Error(`Local Data Profile '${this._id}' uses relative time and requires reevaluateEvery()`);
		}

		return deepFreeze({
			schemaVersion: LOCAL_DATA_PROFILE_SCHEMA_VERSION,
			id: this._id,
			...(this._reevaluateEvery ? {reevaluateEvery: this._reevaluateEvery} : {}),
			scopes: cloneJson(this._scopes),
		});
	}

	private _appendScope(scope: SerializedReplicationScope) {
		requireNonEmptyString(scope.id, 'Replication Scope ID');
		if (this._scopes.some(existing => existing.id === scope.id)) {
			throw new Error(`Duplicate Replication Scope '${scope.id}'`);
		}

		if (scope.kind === 'document' && scope.prune.length > 0) {
			throw new Error(`Document scope '${scope.id}' cannot prune descendants`);
		}

		this._scopes.push(scope);
		return this;
	}
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const LocalDataProfile = {
	define(id: string) {
		return new LocalDataProfileBuilder(id);
	},
};

export function validateLocalDataProfile(profile: SerializedLocalDataProfile): SerializedLocalDataProfile {
	if (!profile || typeof profile !== 'object') {
		throw new Error('Local Data Profile must be an object');
	}

	if (profile.schemaVersion !== LOCAL_DATA_PROFILE_SCHEMA_VERSION) {
		throw new Error(`Unsupported Local Data Profile schema version '${profile.schemaVersion}'`);
	}

	const builder = LocalDataProfile.define(profile.id);
	if (profile.reevaluateEvery) {
		builder.reevaluateEvery(profile.reevaluateEvery);
	}

	for (const scope of profile.scopes ?? []) {
		const options = {access: scope.access, maxDepth: scope.maxDepth, prune: scope.prune, references: scope.references};
		if (scope.kind === 'document') {
			builder.document(scope.id, scope.anchorId, options);
		} else if (scope.kind === 'tree' && scope.anchorFormId) {
			builder.treeByForm(scope.id, scope.anchorFormId, options);
		} else if (scope.kind === 'tree') {
			builder.tree(scope.id, scope.anchorId, options);
		} else if (scope.kind === 'query') {
			builder.scope(scope.id, scope.selector, options);
		} else {
			throw new Error(`Unsupported Replication Scope kind '${(scope as any).kind}'`);
		}
	}

	return builder.toProfile();
}

export function canonicalizeLocalDataProfile(profile: SerializedLocalDataProfile): string {
	return JSON.stringify(sortObjectKeys(validateLocalDataProfile(profile)));
}

function normalizeSelector(selector: Query | QueryBuilder, scopeId: string): Query {
	if (!selector || (!isQueryBuilder(selector) && typeof selector !== 'object')) {
		throw new Error(`Replication Scope '${scopeId}' requires a query selector`);
	}

	const query = cloneJson(toQuery(selector));
	if ((query.findOptions?.sort !== null && query.findOptions?.sort !== undefined)
		|| (query.findOptions?.size !== null && query.findOptions?.size !== undefined)) {
		throw new Error(`Replication Scope '${scopeId}' cannot use sort() or take()`);
	}

	delete query.findOptions;
	return query;
}

function normalizePrune(scopeId: string, prune: Array<Query | QueryBuilder> = []): Query[] {
	if (!Array.isArray(prune)) {
		throw new TypeError(`Replication Scope '${scopeId}' prune must be an array`);
	}

	return prune.map((query, index) => normalizeSelector(query, `${scopeId}' prune #${index + 1}`));
}

function normalizeReferences(references: ReferenceReplicationOptions | undefined, scopeId: string) {
	if (!references) {
		return undefined;
	}

	if (references.mode !== 'typed-data') {
		throw new Error(`Replication Scope '${scopeId}' only supports typed-data references`);
	}

	requirePositiveInteger(references.maxDepth, `Replication Scope '${scopeId}' reference maxDepth`, true);
	const normalized: ReferenceReplicationOptions = {mode: references.mode, maxDepth: references.maxDepth};
	return normalized;
}

function normalizeTreeDepth(maxDepth: number | undefined, scopeId: string) {
	if (maxDepth === null || maxDepth === undefined) {
		return undefined;
	}

	requirePositiveInteger(maxDepth, `Replication Scope '${scopeId}' maxDepth`, true);
	return maxDepth;
}

function assertDescendantSelector(query: Query, scopeId: string) {
	const descendantConditions = Array.from(enumerateConditions(query.graph))
		.filter(condition => condition.field === 'a' && condition.termPrefix !== '-');
	if (descendantConditions.length !== 1) {
		throw new Error(`Replication Scope '${scopeId}' must contain exactly one descendants() anchor`);
	}
}

function * enumerateConditions(expression: any): IterableIterator<any> {
	if (!expression || typeof expression !== 'object') {
		return;
	}

	if (typeof expression.field === 'string') {
		yield expression;
		return;
	}

	for (const condition of expression.conditions ?? []) {
		yield * enumerateConditions(condition);
	}
}

function scopeUsesRelativeTime(scope: SerializedReplicationScope) {
	return [scope.selector, ...scope.prune].filter(Boolean).some(query => {
		const values = Array.isArray(query.parameters) ? query.parameters : Object.values(query.parameters ?? {});
		return values.flat().some(value => typeof value === 'string' && /(^|\b)now(?:$|[+-])/i.test(value));
	});
}

function normalizeIsoDuration(value: string) {
	const duration = requireNonEmptyString(value, 'Reevaluation cadence').toUpperCase();
	const match = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/.exec(duration);
	if (!match || !match.slice(1).some(Boolean)) {
		throw new Error(`Invalid ISO 8601 reevaluation cadence '${value}'`);
	}

	if (match.slice(1).map(value => Number(value ?? 0)).every(value => value === 0)) {
		throw new Error('Reevaluation cadence must be greater than zero');
	}

	return duration;
}

function formatDuration(value: number, unit: DurationUnit) {
	const suffix = {seconds: 'S', minutes: 'M', hours: 'H', days: 'D'}[unit];
	return unit === 'days' ? `P${value}${suffix}` : `PT${value}${suffix}`;
}

function requireNonEmptyString(value: string, label: string) {
	if (typeof value !== 'string' || !value.trim()) {
		throw new Error(`${label} must be a non-empty string`);
	}

	return value.trim();
}

function requirePositiveInteger(value: number, label: string, allowZero = false) {
	if (!Number.isSafeInteger(value) || value < (allowZero ? 0 : 1)) {
		throw new Error(`${label} must be ${allowZero ? 'a non-negative' : 'a positive'} integer`);
	}
}

function cloneJson<T>(value: T): T {
	// JSON serialization is the profile's wire contract and deliberately strips undefined values.
	// eslint-disable-next-line @typescript-eslint/no-unsafe-return
	return JSON.parse(JSON.stringify(value));
}

function deepFreeze<T>(value: T): T {
	if (value && typeof value === 'object' && !Object.isFrozen(value)) {
		Object.freeze(value);
		for (const child of Object.values(value as Record<string, unknown>)) {
			deepFreeze(child);
		}
	}

	return value;
}

function sortObjectKeys(value: unknown): unknown {
	if (Array.isArray(value)) {
		return value.map(item => sortObjectKeys(item));
	}

	if (!value || typeof value !== 'object') {
		return value;
	}

	const record = value as Record<string, unknown>;
	return Object.fromEntries(Object.keys(record).sort().map(key => [key, sortObjectKeys(record[key])]));
}
